import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

const API = '/api';

const ListenContext = createContext(null);

export const useListenContext = () => {
  const ctx = useContext(ListenContext);
  if (!ctx) throw new Error('useListenContext must be within ListenProvider');
  return ctx;
};

export const ListenProvider = ({ children }) => {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [pendingApproval, setPendingApproval] = useState(null);
  const [statusText, setStatusText] = useState('');

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceCheckRef = useRef(null);
  const lastSoundRef = useRef(Date.now());

  const SILENCE_THRESHOLD = 0.01;
  const SILENCE_TIMEOUT_MS = 10000;

  const cleanup = useCallback(() => {
    if (silenceCheckRef.current) { clearInterval(silenceCheckRef.current); silenceCheckRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    analyserRef.current = null;
  }, []);

  const processAudio = useCallback(async (blob) => {
    if (blob.size < 1000) {
      setStatusText('Too short');
      toast.info('Recording too short. Try speaking longer.');
      setTimeout(() => setStatusText(''), 2000);
      return;
    }

    setIsProcessing(true);
    setStatusText('Transcribing...');

    try {
      const formData = new FormData();
      formData.append('audio', blob, 'listen.webm');
      const transcribeRes = await fetch(`${API}/ai/transcribe`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!transcribeRes.ok) {
        toast.error('Transcription failed');
        setStatusText('Error');
        return;
      }
      const { text } = await transcribeRes.json();
      if (!text?.trim()) {
        toast.info('No speech detected.');
        setStatusText('No speech');
        setTimeout(() => setStatusText(''), 2000);
        return;
      }

      setStatusText(`Executing: "${text.trim().substring(0, 40)}..."`);
      toast.info(`Heard: "${text.trim()}"`, { duration: 3000 });

      // Start a new conversation for each listen session
      const chatRes = await fetch(`${API}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text.trim() })
      });

      if (chatRes.ok) {
        const data = await chatRes.json();
        setConversationId(data.conversation_id);

        const response = data.response || '';
        const toolCount = data.tool_calls_made || 0;
        const preview = response.length > 150 ? response.substring(0, 150) + '...' : response;

        if (toolCount > 0) {
          toast.success(`Executed ${toolCount} action${toolCount > 1 ? 's' : ''}`, {
            description: preview,
            duration: 6000
          });
          setStatusText(`Done (${toolCount} actions)`);
        } else {
          toast.success('Response ready', { description: preview, duration: 5000 });
          setStatusText('Done');
        }

        if (data.pending_actions?.length > 0) {
          setPendingApproval({
            conversationId: data.conversation_id,
            actions: data.pending_actions,
            message: text.trim(),
            response
          });
        }
      } else {
        toast.error('AI processing failed');
        setStatusText('Error');
      }
    } catch (err) {
      console.error('Listen mode error:', err);
      toast.error('Voice command failed: ' + err.message);
      setStatusText('Error');
    } finally {
      setIsProcessing(false);
      setTimeout(() => setStatusText(''), 4000);
    }
  }, []);

  const stopListening = useCallback(() => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      setIsListening(false);
      cleanup();
      return;
    }
    setStatusText('Processing...');
    const mediaRecorder = mediaRecorderRef.current;
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      cleanup();
      setIsListening(false);
      processAudio(blob);
    };
    mediaRecorder.stop();
  }, [cleanup, processAudio]);

  const startListening = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 }
      });
      streamRef.current = stream;

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;

      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.start(100);
      setIsListening(true);
      setConversationId(null);
      setStatusText('Listening...');
      lastSoundRef.current = Date.now();

      const dataArray = new Float32Array(analyser.frequencyBinCount);
      silenceCheckRef.current = setInterval(() => {
        if (!analyserRef.current) return;
        analyserRef.current.getFloatTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i] * dataArray[i];
        const rms = Math.sqrt(sum / dataArray.length);
        if (rms > SILENCE_THRESHOLD) lastSoundRef.current = Date.now();
        if (Date.now() - lastSoundRef.current > SILENCE_TIMEOUT_MS) {
          setStatusText('Silence detected...');
          stopListening();
        }
      }, 500);

    } catch (err) {
      if (err.name === 'NotAllowedError') toast.error('Microphone access denied.');
      else toast.error('Could not access microphone: ' + err.message);
      cleanup();
    }
  }, [cleanup, stopListening]);

  const toggleListening = useCallback(() => {
    if (isListening) stopListening();
    else if (!isProcessing) startListening();
  }, [isListening, isProcessing, startListening, stopListening]);

  const dismissApproval = useCallback(() => setPendingApproval(null), []);

  const approveAction = useCallback(async (actionIndex) => {
    if (!pendingApproval) return;
    try {
      const action = pendingApproval.actions[actionIndex];
      const res = await fetch(`${API}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: `Yes, approve and execute: ${action.description || action.action || 'this action'}`,
          conversation_id: pendingApproval.conversationId
        })
      });
      if (res.ok) toast.success('Action approved and executed');
      else toast.error('Failed to approve action');
    } catch (err) {
      toast.error('Approval failed: ' + err.message);
    }
    setPendingApproval(null);
  }, [pendingApproval]);

  return (
    <ListenContext.Provider value={{
      isListening, isProcessing, conversationId, statusText,
      pendingApproval, toggleListening, dismissApproval, approveAction
    }}>
      {children}
    </ListenContext.Provider>
  );
};
